import React from 'react';
import { DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styles from './css/index.module.css';
import ProjectCard from './ProjectCard';

const KanbanBoard = ({
    projects,
    onStatusChange,
    user,
    handleEditProject,
    handleArchiveProject,
    openDuplicateConfirm,
    duplicateJobs,
    setTile,
    deleteModalRef,
    handleChangeProject,
    getStatusColor,
    statusOptions,
    calculateColorSM,
    formatLastUpdated,
    convertHtmlToText,
    getTotalFileCount,
    kanbanStatuses,
}) => {
    // Ensure projects is always an array
    const safeProjects = React.useMemo(() => (Array.isArray(projects) ? projects : []), [projects]);

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 5,
            },
        }),
    );

    // Active drag state
    const [activeId, setActiveId] = React.useState(null);
    const containerRef = React.useRef(null);

    // Group projects by status
    const getProjectsByStatus = status => {
        return safeProjects.filter(project => {
            if (status === 'Undefined') {
                return !project.status || project.status === '' || project.status === null || project.status === undefined;
            }
            return project.status === status;
        });
    };

    const handleDragStart = event => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = event => {
        const { active, over } = event;
        setActiveId(null);

        if (!over) return;

        const activeId = active.id;
        const overId = over.id;

        const project = safeProjects.find(p => p.id.toString() === activeId);
        if (!project) {
            console.warn('Project not found for activeId:', activeId);
            return;
        }

        let newStatus;
        const validStatuses = Array.isArray(kanbanStatuses) && kanbanStatuses.length ? kanbanStatuses : ['Undefined'];
        if (validStatuses.includes(overId)) {
            newStatus = overId;
        } else {
            const overProject = safeProjects.find(p => p.id.toString() === overId);
            if (overProject) {
                newStatus = overProject.status || 'Undefined';
            } else {
                return;
            }
        }

        if (onStatusChange && project.status !== newStatus) {
            const statusToUpdate = newStatus === 'Undefined' ? null : newStatus;
            onStatusChange(project.id, statusToUpdate);
        }
    };

    const activeProject = React.useMemo(() => {
        return safeProjects.find(project => project.id.toString() === activeId);
    }, [activeId, safeProjects]);

    // Helper to get color for a status
    const getStatusBgColor = status => {
        if (status === 'Undefined') return '#bdbdbd';
        const found = statusOptions.find(opt => opt.value === status);
        return found?.color || '#AAA';
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div ref={containerRef} className={styles.kanbanContainer}>
                {(Array.isArray(kanbanStatuses) && kanbanStatuses.length ? kanbanStatuses : ['Undefined']).map(status => {
                    const statusProjects = getProjectsByStatus(status);

                    return (
                        <div key={status} className={styles.kanbanColumn}>
                            <DroppableStatusColumn status={status}>
                                <div className={`${styles.kanbanStatus} ${styles.kanbanHeader}`} style={{ background: getStatusBgColor(status) }}>
                                    <span> {status}</span> <span style={{ fontWeight: 400 }}> {statusProjects.length}</span>
                                </div>
                                <div className={`${styles.kanbanContent} ${styles.hideScrollbar}`}>
                                    <SortableContext items={statusProjects.map(project => project.id.toString())} strategy={verticalListSortingStrategy}>
                                        {statusProjects.map(project => (
                                            <SortableProjectCard
                                                key={project.id}
                                                project={project}
                                                user={user}
                                                handleEditProject={handleEditProject}
                                                handleArchiveProject={handleArchiveProject}
                                                openDuplicateConfirm={openDuplicateConfirm}
                                                duplicateJobs={duplicateJobs}
                                                setTile={setTile}
                                                deleteModalRef={deleteModalRef}
                                                handleChangeProject={handleChangeProject}
                                                getStatusColor={getStatusColor}
                                                statusOptions={statusOptions}
                                                calculateColorSM={calculateColorSM}
                                                formatLastUpdated={formatLastUpdated}
                                                convertHtmlToText={convertHtmlToText}
                                                getTotalFileCount={getTotalFileCount}
                                            />
                                        ))}
                                    </SortableContext>
                                </div>
                            </DroppableStatusColumn>
                        </div>
                    );
                })}
            </div>
            <DragOverlay>
                {activeProject ? (
                    <div className={styles.kanbanCardWrapper} style={{ opacity: 0.8, transform: 'rotate(5deg)' }}>
                        <ProjectCard
                            item={activeProject}
                            user={user}
                            handleEditProject={handleEditProject}
                            handleArchiveProject={handleArchiveProject}
                            openDuplicateConfirm={openDuplicateConfirm}
                            duplicateJobs={duplicateJobs}
                            setTile={setTile}
                            deleteModalRef={deleteModalRef}
                            handleChangeProject={handleChangeProject}
                            getStatusColor={getStatusColor}
                            statusOptions={statusOptions}
                            calculateColorSM={calculateColorSM}
                            formatLastUpdated={formatLastUpdated}
                            convertHtmlToText={convertHtmlToText}
                            getTotalFileCount={getTotalFileCount}
                            kanban={true}
                        />
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};

// DroppableStatusColumn component
const DroppableStatusColumn = ({ status, children }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: status,
    });

    return (
        <>
            {React.Children.map(children, (child, index) => {
                if (index === 1) {
                    // Clone the content div and apply droppable props
                    return React.cloneElement(child, {
                        ref: setNodeRef,
                        style: {
                            ...child.props.style,
                            backgroundColor: isOver ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                        },
                    });
                }
                return child;
            })}
        </>
    );
};

// SortableProjectCard component
const SortableProjectCard = ({
    project,
    user,
    handleEditProject,
    handleArchiveProject,
    openDuplicateConfirm,
    duplicateJobs,
    setTile,
    deleteModalRef,
    handleChangeProject,
    getStatusColor,
    statusOptions,
    calculateColorSM,
    formatLastUpdated,
    convertHtmlToText,
    getTotalFileCount,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: project.id.toString() });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={styles.kanbanCardWrapper}>
            <ProjectCard
                item={project}
                user={user}
                handleEditProject={handleEditProject}
                handleArchiveProject={handleArchiveProject}
                openDuplicateConfirm={openDuplicateConfirm}
                duplicateJobs={duplicateJobs}
                setTile={setTile}
                deleteModalRef={deleteModalRef}
                handleChangeProject={handleChangeProject}
                getStatusColor={getStatusColor}
                statusOptions={statusOptions}
                calculateColorSM={calculateColorSM}
                formatLastUpdated={formatLastUpdated}
                convertHtmlToText={convertHtmlToText}
                getTotalFileCount={getTotalFileCount}
                kanban={true}
            />
        </div>
    );
};

export default KanbanBoard;
